/**
 * Product Feature Carousel
 *
 * Synchronizes three Splide carousels:
 * - Lifestyle image slider
 * - Product image slider (main)
 * - Thumbnail navigation slider
 */

class ProductFeatureCarousel {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;

    this.lifestyleSlider = null;
    this.productSlider = null;
    this.thumbnailSlider = null;

    this.init();
  }

  init() {
    // Wait for Splide to be available
    if (typeof Splide === 'undefined') {
      document.addEventListener('DOMContentLoaded', () => this.initSliders());
      return;
    }
    this.initSliders();
  }

  initSliders() {
    const lifestyleEl = this.container.querySelector(`#lifestyle-slider-${this.sectionId}`);
    const productEl = this.container.querySelector(`#product-slider-${this.sectionId}`);
    const thumbnailEl = this.container.querySelector(`#thumbnail-slider-${this.sectionId}`);

    if (!lifestyleEl || !productEl || !thumbnailEl) {
      return;
    }

    // Initialize lifestyle slider (fade effect)
    this.lifestyleSlider = new Splide(lifestyleEl, {
      type: 'fade',
      rewind: true,
      pagination: false,
      arrows: false,
      drag: false,
    });

    // Initialize product slider (main carousel with custom arrows)
    this.productSlider = new Splide(productEl, {
      type: 'fade',
      rewind: true,
      pagination: false,
      arrows: false,
      drag: true,
    });

    // Initialize thumbnail slider with autoWidth to respect CSS-defined thumbnail sizes
    this.thumbnailSlider = new Splide(thumbnailEl, {
      type: 'loop',
      rewind: true,
      pagination: false,
      arrows: false,
      isNavigation: true,
      autoWidth: true,
      gap: '0.8rem',
      drag: true,
    });

    // Sync all sliders
    this.productSlider.sync(this.lifestyleSlider);
    this.productSlider.sync(this.thumbnailSlider);

    // Mount all sliders
    this.lifestyleSlider.mount();
    this.productSlider.mount();
    this.thumbnailSlider.mount();

    // Add custom arrow functionality
    this.setupCustomArrows();
    this.setupWheelNavigation(thumbnailEl, this.thumbnailSlider);
  }

  setupCustomArrows() {
    const prevButton = this.container.querySelector('.product-feature-carousel__arrow--prev');
    const nextButton = this.container.querySelector('.product-feature-carousel__arrow--next');

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        this.productSlider.go('<');
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        this.productSlider.go('>');
      });
    }

    // Update arrow states on move
    this.productSlider.on('move', () => {
      this.updateArrowStates();
    });

    // Initial arrow state
    this.updateArrowStates();
  }

  updateArrowStates() {
    const prevButton = this.container.querySelector('.product-feature-carousel__arrow--prev');
    const nextButton = this.container.querySelector('.product-feature-carousel__arrow--next');

    // Since we're using rewind, arrows are always enabled
    // But if you want to disable at edges, you can check:
    // const index = this.productSlider.index;
    // const length = this.productSlider.length;
  }

  setupWheelNavigation(carousel, splide) {
    const track = carousel.querySelector('.splide__track');
    const list = carousel.querySelector('.splide__list');
    if (!track || !list) return;

    let snapTimeout = null;

    track.addEventListener(
      'wheel',
      (e) => {
        // Only respond to horizontal scroll (trackpad horizontal swipe)
        if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
        if (Math.abs(e.deltaX) < 2) return;

        const { Move, Controller } = splide.Components;
        const position = Move.getPosition();
        const endPosition = Move.toPosition(Controller.getEnd());

        // Allow natural page scroll at carousel boundaries
        if (e.deltaX > 0 && position <= endPosition) return;
        if (e.deltaX < 0 && position >= 0) return;

        e.preventDefault();

        // Disable CSS transitions for direct 1:1 tracking with the finger
        list.style.transition = 'none';

        // Move the track directly by the scroll delta, clamped to boundaries
        const newPosition = Math.max(
          endPosition,
          Math.min(0, position - e.deltaX)
        );
        Move.translate(newPosition);

        // Snap to nearest slide when scrolling stops
        if (snapTimeout) clearTimeout(snapTimeout);
        snapTimeout = setTimeout(() => {
          // Re-enable transitions so the snap animates smoothly
          list.style.transition = '';

          const currentPos = Move.getPosition();
          const endIdx = Controller.getEnd();
          let closestIndex = 0;
          let closestDist = Infinity;

          for (let i = 0; i <= endIdx; i++) {
            const dist = Math.abs(currentPos - Move.toPosition(i));
            if (dist < closestDist) {
              closestDist = dist;
              closestIndex = i;
            }
          }

          splide.go(closestIndex);
        }, 150);
      },
      { passive: false }
    );
  }

  destroy() {
    if (this.lifestyleSlider) {
      this.lifestyleSlider.destroy();
    }
    if (this.productSlider) {
      this.productSlider.destroy();
    }
    if (this.thumbnailSlider) {
      this.thumbnailSlider.destroy();
    }
  }
}

// Initialize all carousels on the page
function initProductFeatureCarousels() {
  const carousels = document.querySelectorAll('.product-feature-carousel');
  carousels.forEach((carousel) => {
    if (!carousel.dataset.initialized) {
      new ProductFeatureCarousel(carousel);
      carousel.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductFeatureCarousels);
} else {
  initProductFeatureCarousels();
}

// Re-initialize for Shopify section rendering
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (section.classList.contains('section--product-feature-carousel')) {
    const carousel = section.querySelector('.product-feature-carousel');
    if (carousel) {
      carousel.dataset.initialized = '';
      new ProductFeatureCarousel(carousel);
      carousel.dataset.initialized = 'true';
    }
  }
});
